import { useEffect, useState } from 'react'
import { useLocation } from 'wouter'
import {
	applyActionCode,
} from 'firebase/auth'
import { auth } from '@/constants'
import { CheckCircle, Loader2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { apiFetch, parseFirebaseErrorCode } from '@/utils'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage
} from '@/components/ui/form'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Card } from '@/components/ui/card'
import logo from '@/assets/logo-login.svg'
import { Label } from '@/components/ui/label'

const signupFormSchema = z
	.object({
		password: z
			.string()
			.min(8, 'Password must be at least 8 characters')
			.regex(
				/^(?=.*[!@#$%^&*-])(?=.*[0-9])(?=.*[A-Z]).{8,}$/,
				'Password must contain at least one uppercase letter, one number, and one special character'
			),
		confirmPassword: z.string().min(8, 'Password confirmation is required')
	})
	.refine((data) => data.password === data.confirmPassword, {
		message: "Passwords don't match",
		path: ['confirmPassword']
	})

type IChangePassword = z.infer<typeof signupFormSchema>

export default function AuthActionsPage() {
	const [mode, setMode] = useState<string | null>(null)
	const [oobCode, setOobCode] = useState<string | null>(null)
	const [status, setStatus] = useState<
		'loading' | 'success' | 'error' | 'form'
	>('loading')
	const [error, setError] = useState<string | null>(null)
	const [isSubmitting, setIsSubmitting] = useState(false)

	const [location, navigate] = useLocation()
	const form = useForm<IChangePassword>({
		resolver: zodResolver(signupFormSchema),
		defaultValues: {
			password: '',
			confirmPassword: ''
		}
	})


	// Determine if this is the native app handoff route
	const isHandoffRoute = window.location.pathname === '/change-email-confirm'

	useEffect(() => {
		const searchParams = new URLSearchParams(window.location.search)
		const modeParam = searchParams.get('mode')
		const oobCodeParam = searchParams.get('oobCode')

		setMode(modeParam)
		setOobCode(oobCodeParam)

		// /change-email-confirm is now just the native app landing — show success immediately
		if (isHandoffRoute) {
			setStatus('success')
			return
		}

		if(!modeParam || !oobCodeParam) {
			setStatus('error')
			setError('Invalid link. Please use the correct link from your email.')
			return
		}

		if(modeParam === 'verifyEmail') {
			const verifyEmail = async () => {
				try {
					await applyActionCode(auth, oobCodeParam)
					setStatus('success')
				} catch(error: any) {
					console.error('Email verification error:', error)
					const message = parseFirebaseErrorCode(error?.code)
					setStatus('error')
					setError(`Email verification failed. ${message}`)
				}
			}
			verifyEmail()
		}

		if (modeParam === 'resetPassword') {
			setStatus('form')
		}
	}, [location])

	const handlePasswordReset = async (data: IChangePassword) => {
		const { password, confirmPassword } = data

		if(password !== confirmPassword) {
			setError('Passwords do not match.')
			return
		}

		if(!oobCode) return

		try {
			setError(null)
			setIsSubmitting(true)
			//   await confirmPasswordReset(auth, oobCode, password)
			const response = await apiFetch('/v1/users/password-reset/confirm', {
				method: 'POST',
				body: JSON.stringify({
					oobCode,
					newPassword: password
				})
			})

			const result = await response.json()

			if(!result.success) {
				setError(`Password reset failed. Please, double check your reset link. (${result.error?.message ?? 'Unknown error'})`)
			} else {
				setStatus('success')
			}

		} catch(error: any) {
			setStatus('error')
			setError(`Password reset failed. Please double check your link.`)
		} finally {
			setIsSubmitting(false)
		}
	}

	return (
		<div className="min-h-auto h-full flex items-end justify-between login-page flex-col relative">
			<div className="flex flex-col justify-center items-center w-full h-[calc(100vh-414px)]">
				<img src={logo} alt="" />
				<p className="text-[20px] leading-6 font-bold text-[#ffffff] font-montserrat mt-1 mb-1">
					SECH
				</p>
				<p className="text-xs leading-[14px] font-montserrat font-medium text-[#FFFFFF]">
					Digital Wallet
				</p>
			</div>
			<div className="max-w-[1024px] w-full">
				<Card className="bg-[#2F2F32] border-0 p-6 rounded-tl-[36px] rounded-tr-[36px] rounded-b-none">
					{status === 'loading' && (
						<div className="flex flex-col items-center gap-3 text-center">
							<Loader2 className="h-8 w-8 animate-spin text-[#EF4444]" />
							<h2 className="text-lg font-semibold text-[#ffffff]">
								{mode === 'verifyEmail'
									? 'Verifying your email...'
									: 'Preparing password reset...'}
							</h2>
							<p className="text-[#BABABB] text-sm">Please wait a moment.</p>
						</div>
					)}

					{status === 'form' && (
						<>
							<div className="text-center mb-4">
								<h1 className="text-[20px] leading-[26px] font-semibold text-[#ffffff] font-figtree">Change Password</h1>
								<p className="text-xs leading-4 font-regular font-figtree text-[#AFAFAF] mt-2">
									Here you can set your new password.
								</p>
							</div>

							<Form {...form}>
								<form
									onSubmit={form.handleSubmit(handlePasswordReset)}>
									<FormField
										control={form.control}
										name="password"
										render={({ field }) => (
											<FormItem className="mb-6">
												<Label className="text-[13px] leading-4 font-medium font-figtree mb-2 text-[#ffffff]">
													Password
												</Label>
												<FormControl>
													<Input
														type="password"
														{...field}
														autoComplete="new-password"
														placeholder="minimum 8 symbols"
														className="bg-[#212124] border-0 h-[42px] rounded-[8px] text-[#ffffff]
																	focus-visible:ring-2 focus-visible:ring-[#6B6565]"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									<FormField
										control={form.control}
										name="confirmPassword"
										render={({ field }) => (
											<FormItem className="mb-3">
												<Label className="text-[13px] leading-4 font-medium font-figtree mb-2 text-[#ffffff]">
													Confirm Password
												</Label>
												<FormControl>
													<Input
														type="password"
														{...field}
														autoComplete="new-password"
														placeholder="repeat password"
														className="bg-[#212124] border-0 h-[42px] rounded-[8px] text-[#ffffff]
                                       								focus-visible:ring-2 focus-visible:ring-[#6B6565]"
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>

									{error && (
										<p className="text-xs text-[#EF4444] mb-3">{error}</p>
									)}

									<Button type="submit" className="w-full h-[42px] text-sm font-medium rounded-[21px] bg-[#EF4444]" disabled={isSubmitting}>
										{isSubmitting ? (
											<>
												<Loader2 className="h-4 w-4 animate-spin mr-2" />
												Changing password...
											</>
										) : (
											'Change Password'
										)}
									</Button>
								</form>
							</Form>

							<div className="mt-4 text-center space-y-2">
								<p className="text-sm text-[#BABABB]">
									Remember your password?{' '}
									<Button
										variant="link"
										className="p-0 h-auto font-semibold text-[#EF4444] hover:text-[#DC2626]"
										onClick={() => navigate('/login')}>
										Log in
									</Button>
								</p>
								{/* <Button
								variant="link"
								className="text-sm text-muted-foreground"
								onClick={() => navigate('/')}>
								Back to Home
								</Button> */}
							</div>
						</>
					)}

					{status === 'success' && (
						<div className="flex flex-col items-center gap-4 text-center">
							<CheckCircle className="h-12 w-12 text-green-500" />
							<h2 className="text-xl font-bold text-[#ffffff]">
								{mode === 'verifyEmail'
									? 'Email Verified!'
									: mode === 'resetPassword'
										? 'Password Changed Successfully!'
										: 'Success!'}
							</h2>
							<p className="text-xs text-[#AFAFAF]">
								{mode === 'verifyEmail'
									? 'Your email has been successfully confirmed. You can now sign in.'
									: mode === 'resetPassword'
										? 'Your password has been changed. You can now sign in with your new password.'
										: 'You can now sign in.'}
							</p>
							{isHandoffRoute ? (
								<Button className="w-full bg-[#EF4444]" onClick={() => navigate('/login')}>
									Go to Login
								</Button>
							) : (
								<Button
									className="w-full bg-[#EF4444]"
									onClick={() => { window.location.href = '/change-email-confirm' }}
								>
									Open App & Sign In
								</Button>
							)}
						</div>
					)}

					{status === 'error' && mode === 'verifyEmail' && (
						<div className="flex flex-col items-center gap-4 text-center">
							<XCircle className="h-12 w-12 text-[#EF4444]" />
							<h2 className="text-xl font-bold text-[#ffffff]">Verification Failed</h2>
							<p className="text-xs text-[#AFAFAF]">{error}</p>
							<Button className="w-full bg-[#EF4444]" onClick={() => navigate('/')}>
								Return Home
							</Button>
						</div>
					)}

					{status === 'error' && mode === 'resetPassword' && (
						<div className="flex flex-col items-center gap-4 text-center">
							<XCircle className="h-12 w-12 text-[#EF4444]" />
							<h2 className="text-xl font-bold text-[#ffffff]">Password Reset Failed</h2>
							<p className="text-xs text-[#AFAFAF]">{error}</p>
							<Button className="w-full bg-[#EF4444]" onClick={() => navigate('/forgot-password')}>
								Request New Reset Link
							</Button>
							<Button
								variant="link"
								className="p-0 h-auto font-semibold text-[#EF4444] hover:text-[#DC2626]"
								onClick={() => navigate('/login')}>
								Back to Login
							</Button>
						</div>
					)}
				</Card>
			</div>
		</div>
	)
}